import { useDropzone, DropzoneOptions } from 'react-dropzone';
// @mui
import {
  Box, Stack, Typography, StackProps,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
// assets
import { SxProps, Theme } from '@mui/system';
import { FC } from 'react';
import {
  DataGridPro, GridColDef,
} from '@mui/x-data-grid-pro';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { BaseLabel } from '@mgm/common/components/base';
import UploadIllustration from '../UploadIllustration';
import { TMember } from '../../types';

export interface TUploadArea extends DropzoneOptions {
  error?: boolean;
  sx?: SxProps<Theme>;
  thumbnail?: boolean;
  placeholder?: React.ReactNode;
  disableMultiple?: boolean;
  //
  file?: string | null;
  onDelete?: VoidFunction;
  //
  files?: (File | string)[];
  onUpload?: VoidFunction;
  onRemove?: (file: string) => void;
  onRemoveAll?: VoidFunction;
  data: Array<TMember>
}

function Placeholder({ sx, ...other }: StackProps) {
  const { t } = useTranslation(['settings', 'team']);

  return (
    <Stack
      spacing={5}
      alignItems="center"
      justifyContent="center"
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        width: 1,
        textAlign: {
          xs: 'center',
          md: 'left',
        },
        ...sx,
      }}
      {...other}
    >
      <UploadIllustration sx={{ width: 220 }} />

      <div>
        <Typography gutterBottom variant="h5">
          {t('dropOrSelectFile', { ns: 'team' })}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>

          { t('dropFiles') }
          <u className="px-1">{ t('clickHere') }</u>
          {t('toUpload')}
        </Typography>
      </div>
    </Stack>
  );
}

const StyledDropZone = styled('div')(({ theme }: any) => ({
  outline: 'none',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${alpha(theme.palette.grey[500], 0.32)}`,
  '&:hover': {
    opacity: 0.72,
  },
}));

// ----------------------------------------------------------------------

const UploadArea: FC<TUploadArea> = ({
  disabled,
  multiple = false,
  error,
  file,
  onDelete,
  files,
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  data,
  sx,
  ...other
}) => {
  const {
    getRootProps, getInputProps, isDragActive, isDragReject,
  } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const { t } = useTranslation(['inputs', 'common']);

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('name'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'permissionLevel',
      headerName: t('permission'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {t(`${row.permission?.toLowerCase()}`, { ns: 'common' })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: t('email'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.email}
          </Typography>
        </Box>
      ),
    },
  ];

  const hasFile = !!file && !multiple;

  const isError = isDragReject || !!error;

  return (
    <Box sx={{ width: 1, position: 'relative' }}>
      <StyledDropZone
        {...getRootProps()}
        sx={{
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(isError && {
            color: 'error.main',
            bgcolor: 'error.lighter',
            borderColor: 'error.light',
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
        }}
      >
        <input {...getInputProps()} />

        {!hasFile && (
        <Placeholder />
        )}

        {hasFile && (
          <Box>
            <BaseLabel letterSpacing="-0.5px">Preview</BaseLabel>
            <DataGridPro
              getRowId={(row) => row.email}
              columns={columns}
              rows={data}
            />
          </Box>
        )}

      </StyledDropZone>

    </Box>
  );
};

export {
  UploadArea,
};
